import { Navigate, useNavigate } from "react-router-dom";

import { ChapterLogo } from "~/common/assets";
import {
  EmptyState,
  LoadingState,
  MailBoxIcon,
  Typography,
} from "~/common/components";
import { PATIENT_CONSULTATION_STATUS } from "~/common/constants";
import {
  CONSULTATIONS_CODES,
  consultationTitle,
  isPatientActive,
  TYPOGRAPHY_VARIANTS,
} from "~/common/utils";
import { ROUTES } from "~/patients/router";
import {
  AppointmentConsultationCard,
  ConsultationCard,
} from "~/patients/screens/consultations";
import {
  useGetCurrentIntakeBundle,
  useGetCurrentScreeningBundle,
} from "../hooks";
import { useGetCurrentPatient } from "../hooks/queries/useGetCurrentPatient";
import { ConsultationScreeningCard } from "./consultations/ConsultationScreeningCard";

export const LeadPatientHomeScreen = () => {
  const navigate = useNavigate();
  const { data: user, isFetching } = useGetCurrentPatient();
  const isInactivePatient = !!user?.organization_patients[0].archived_at;

  const consultationStatus = user?.consultation?.status;

  const isAutoPromotePatient =
    user?.organization_patients[0].invitation_link.auto_promote;

  const hasAppointmentSchedulingIncluded =
    user?.organization_patients[0].invitation_link.scheduling_included;

  const { data: screeningData, isFetching: isFetchingScreening } =
    useGetCurrentScreeningBundle();

  const hasCompletedScreening =
    screeningData?.sections_completed === screeningData?.total_sections;

  const showReviewingConsultationText =
    !isAutoPromotePatient &&
    hasCompletedScreening &&
    (consultationStatus === PATIENT_CONSULTATION_STATUS.screening_started ||
      consultationStatus === PATIENT_CONSULTATION_STATUS.screening_submitted);

  const { data: intakeData, isFetching: isFetchingIntake } =
    useGetCurrentIntakeBundle();

  if (isPatientActive(user?.organization_patients[0].status))
    return <Navigate to={ROUTES.notFound} />;

  if (user && !user.organization_patients[0].email_verified_at)
    return <Navigate to={ROUTES.verifyEmail} />;

  if (
    user &&
    !user.organization_patients[0].settings?.screening_first_screen_visited
  )
    return <Navigate to={ROUTES.leadScreeningIntroScreen} />;

  if (isFetching || isFetchingScreening || isFetchingIntake)
    return <LoadingState />;

  if (isInactivePatient)
    return (
      <EmptyState
        icon={<MailBoxIcon />}
        message="Your consultation has ended. Please contact our team if you have any questions."
      />
    );

  return (
    <div className="flex size-full justify-center">
      <div className="flex size-full flex-col gap-4 px-6 py-8 md:w-2/3 lg:w-1/2">
        {screeningData ? (
          <ConsultationScreeningCard
            completed={hasCompletedScreening}
            title={consultationTitle[CONSULTATIONS_CODES.initial_screening]}
            totalSections={screeningData.total_sections}
            currentSection={screeningData.sections_completed}
            handleCompleteConsultation={() =>
              navigate(ROUTES.completeScreeningConsultation)
            }
            duration={screeningData.duration}
          />
        ) : (
          <EmptyState message="You don't have pending activity yet, we're looking forward to get in touch with you!" />
        )}
        <>
          <ConsultationCard
            completed={
              intakeData?.sections_completed === intakeData?.total_sections
            }
            title={consultationTitle[CONSULTATIONS_CODES.initial_intake]}
            handleCompleteConsultation={() =>
              navigate(ROUTES.leadIntakeIntroScreen)
            }
            isAutoPromotePatient={isAutoPromotePatient}
            intakeData={intakeData}
          />
          {hasAppointmentSchedulingIncluded && (
            <AppointmentConsultationCard
              title="Schedule appointment"
              intakeData={intakeData}
              isAutoPromotePatient={isAutoPromotePatient}
            />
          )}
        </>

        {showReviewingConsultationText && (
          <div className="flex flex-col items-center pt-9">
            <Typography
              className="w-3/4 text-center text-base"
              variant={TYPOGRAPHY_VARIANTS.secondary}
            >
              {hasCompletedScreening &&
                !isAutoPromotePatient &&
                "We are reviewing your request and will be in touch with you very soon."}
            </Typography>
          </div>
        )}
        <div className="flex size-full flex-col items-center justify-end lg:hidden">
          <img
            src={ChapterLogo}
            alt="Chapter Logo"
            className="h-8 object-contain"
          />
        </div>
      </div>
    </div>
  );
};
